import { useViewIfNeeded } from "@/hooks/use-view-if-needed";
import { ViewType } from "@/modes/walk-mode/walk-types";
import { useCached3DObjectIfExists } from "@/object-cache";
import { useAppSelector } from "@/store/store-hooks";
import { isOrthographicCamera } from "@/types/threejs-type-guards";
import {
  selectIElementWorldTransform,
  useControlsLockContext,
} from "@faro-lotv/app-component-toolbox";
import { assert } from "@faro-lotv/foundation";
import { IElementImg2d } from "@faro-lotv/ielement-types";
import { useThree } from "@react-three/fiber";
import { useLayoutEffect, type JSX } from "react";

export type PhotoRendererProps = {
  /** The IElementImg2d element to be rendered */
  iElementImg2d: IElementImg2d;

  /** Which view is this: main view in walk mode, left view, or right view in splitscreen mode */
  viewType: ViewType;
};

/**
 * @returns Renderer for a photo image
 */
export function PhotoRenderer({
  iElementImg2d,
  viewType,
}: PhotoRendererProps): JSX.Element {
  const photoObject = useCached3DObjectIfExists(iElementImg2d, "PhotoRenderer");

  assert(photoObject, "Photo to render must exist!");

  const globalPose = useAppSelector(
    selectIElementWorldTransform(iElementImg2d.id),
  );

  const { isControlsLocked } = useControlsLockContext();

  // move camera to the world position of the target 2D photo element
  // set the camera rotation the same as the target 2D photo element
  // set camera frustum so that the 2D photo will be rendered in full screen
  // set camera near field to 0 so that 2D photo can be seen
  const camera = useThree((s) => s.camera);
  useLayoutEffect(() => {
    if (!isControlsLocked && isOrthographicCamera(camera)) {
      camera.position.fromArray(globalPose.position);
      camera.quaternion.fromArray(globalPose.quaternion);

      camera.top =
        (photoObject.geometry.parameters.height / 2) * globalPose.scale[0];
      camera.right =
        (photoObject.geometry.parameters.width / 2) * globalPose.scale[0];
      camera.left = -camera.right;
      camera.bottom = -camera.top;

      camera.near = 0;
      camera.updateProjectionMatrix();
    }
  }, [
    camera,
    globalPose.position,
    globalPose.quaternion,
    globalPose.scale,
    isControlsLocked,
    photoObject,
  ]);

  const photoToRender = useViewIfNeeded(
    photoObject,
    viewType === ViewType.RightView,
  );

  assert(photoToRender, "Photo to render must exist!");

  return (
    <group {...globalPose}>
      <primitive object={photoToRender} name={iElementImg2d.id} />
    </group>
  );
}
